/**
 * Calendar arithmetic on "day numbers" (days since 1970-01-01, proleptic Gregorian, no timezone)
 * plus Intl-based timezone conversion. No tz database is bundled: `Intl.DateTimeFormat` is
 * available in Node, browsers and Cloudflare Workers.
 */

export interface Time {
  h: number;
  m: number;
}

const MS_PER_DAY = 86_400_000;

export const pad = (n: number, w = 2) => String(n).padStart(w, "0");

export function dayNumber(y: number, m: number, d: number): number {
  return Math.floor(Date.UTC(y, m - 1, d) / MS_PER_DAY);
}

export function ymd(dn: number): { y: number; m: number; d: number } {
  const t = new Date(dn * MS_PER_DAY);
  return { y: t.getUTCFullYear(), m: t.getUTCMonth() + 1, d: t.getUTCDate() };
}

/** ISO weekday: Monday = 1 … Sunday = 7. */
export function weekday(dn: number): number {
  // 1970-01-01 was a Thursday (4).
  return ((((dn + 3) % 7) + 7) % 7) + 1;
}

export function daysInMonth(y: number, m: number): number {
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

export function isValidDate(y: number, m: number, d: number): boolean {
  return m >= 1 && m <= 12 && d >= 1 && d <= daysInMonth(y, m);
}

/** Adds months, clamping the day to the target month's length (Jan 31 + 1 month = Feb 28/29). */
export function addMonths(dn: number, months: number): number {
  const { y, m, d } = ymd(dn);
  const total = y * 12 + (m - 1) + months;
  const ny = Math.floor(total / 12);
  const nm = (total % 12) + 1;
  return dayNumber(ny, nm, Math.min(d, daysInMonth(ny, nm)));
}

export function formatDate(dn: number): string {
  const { y, m, d } = ymd(dn);
  return `${pad(y, 4)}-${pad(m)}-${pad(d)}`;
}

export function formatDateTime(dn: number, t: Time): string {
  return `${formatDate(dn)}T${pad(t.h)}:${pad(t.m)}:00`;
}

/** Start of the week containing `dn`, for a week starting on ISO weekday `startDay`. */
export function weekStart(dn: number, startDay: number): number {
  return dn - ((weekday(dn) - startDay + 7) % 7);
}

export const isWorkday = (dn: number) => weekday(dn) <= 5;

/** `n`th workday (Mon–Fri) of a month; negative counts from the end. Null when out of range. */
export function nthWorkdayOfMonth(y: number, m: number, n: number): number | null {
  const first = dayNumber(y, m, 1);
  const last = dayNumber(y, m, daysInMonth(y, m));
  const days: number[] = [];
  for (let d = first; d <= last; d++) if (isWorkday(d)) days.push(d);
  return (n > 0 ? days[n - 1] : days[days.length + n]) ?? null;
}

/** `n`th ISO weekday `wd` of a month (n = -1 for the last one). Null when it doesn't exist. */
export function nthWeekdayOfMonth(y: number, m: number, n: number, wd: number): number | null {
  if (n > 0) {
    const first = dayNumber(y, m, 1);
    const d = first + ((wd - weekday(first) + 7) % 7) + (n - 1) * 7;
    return ymd(d).m === m ? d : null;
  }
  const last = dayNumber(y, m, daysInMonth(y, m));
  const d = last - ((weekday(last) - wd + 7) % 7) + (n + 1) * 7;
  return ymd(d).m === m ? d : null;
}

// ---------------------------------------------------------------------------
// Timezones
//
// Two interchangeable sources answer "what is the wall clock of this instant in this zone":
// `Intl.DateTimeFormat` (Node, browsers, Workers, JavaScriptCore, Hermes) and, for engines built
// without Intl (stock QuickJS on Android), a host callback `globalThis.__host_tzOffset(tz, epochMs)`
// returning the zone's UTC offset in milliseconds (null/NaN or a throw for an unknown zone),
// implemented natively with java.time / Foundation. Everything else goes through `wallClock`.

type HostTzOffset = (tz: string, epochMs: number) => number | null | undefined;

/** Looked up at call time, so a host may install the callback after loading the code. */
const hostTzOffset = (): HostTzOffset | undefined => {
  const f = (globalThis as { __host_tzOffset?: unknown }).__host_tzOffset;
  return typeof f === "function" ? (f as HostTzOffset) : undefined;
};

const hasIntl = () => typeof Intl !== "undefined" && typeof Intl.DateTimeFormat === "function";

/** Which source answers timezone questions: `intl`, `host`, or `none` (only UTC works then). */
export function timezoneSource(): "intl" | "host" | "none" {
  return hasIntl() ? "intl" : hostTzOffset() ? "host" : "none";
}

/** Offset of `tz` at `epochMs` from the host callback; throws for an unknown zone. */
function hostOffset(tz: string, epochMs: number): number {
  const host = hostTzOffset();
  let off: number | null | undefined;
  if (host) off = host(tz, epochMs);
  else if (tz === "UTC" || tz === "Etc/UTC" || tz === "GMT") off = 0;
  else throw new RangeError("No Intl and no __host_tzOffset: only UTC is available");
  if (typeof off !== "number" || !Number.isFinite(off)) throw new RangeError(`Invalid time zone: ${tz}`);
  return off;
}

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatter(tz: string): Intl.DateTimeFormat {
  let f = formatters.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    formatters.set(tz, f);
  }
  return f;
}

export function isValidTimezone(tz: string): boolean {
  try {
    if (hasIntl()) formatter(tz);
    else hostOffset(tz, Date.now());
    return true;
  } catch {
    return false;
  }
}

/** Wall clock of `instant` in `tz`. */
export function wallClock(instant: Date, tz: string): { dn: number; h: number; m: number; s: number } {
  if (!hasIntl()) {
    const ms = instant.getTime();
    // Floor to the second, like Intl's formatted output.
    const local = Math.floor((ms + hostOffset(tz, ms)) / 1000) * 1000;
    const t = new Date(local);
    return { dn: Math.floor(local / MS_PER_DAY), h: t.getUTCHours(), m: t.getUTCMinutes(), s: t.getUTCSeconds() };
  }
  const parts = formatter(tz).formatToParts(instant);
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? 0);
  return {
    dn: dayNumber(get("year"), get("month"), get("day")),
    h: get("hour") % 24,
    m: get("minute"),
    s: get("second"),
  };
}

/** Wall-clock `YYYY-MM-DDTHH:MM:SS` of `instant` in `tz`. */
export function toLocal(instant: Date, tz: string): string {
  const w = wallClock(instant, tz);
  return formatDateTime(w.dn, w).slice(0, 17) + pad(w.s);
}

function offsetMs(instant: number, tz: string): number {
  const w = wallClock(new Date(instant), tz);
  const asUtc = w.dn * MS_PER_DAY + ((w.h * 60 + w.m) * 60 + w.s) * 1000;
  return asUtc - Math.floor(instant / 1000) * 1000;
}

/**
 * UTC instant of a wall-clock time in `tz`. Ambiguous times (DST fall-back) resolve to the
 * earlier instant; nonexistent times (spring-forward gap) are shifted forward by the gap, the
 * way most calendar software does it (02:30 → 03:30).
 */
export function fromWallClock(dn: number, t: Time, tz: string, seconds = 0): Date {
  const asUtc = dn * MS_PER_DAY + ((t.h * 60 + t.m) * 60 + seconds) * 1000;
  const before = asUtc - offsetMs(asUtc - MS_PER_DAY, tz);
  const after = asUtc - offsetMs(asUtc + MS_PER_DAY, tz);
  const valid = [before, after].filter((c) => offsetMs(c, tz) === asUtc - c).sort((x, y) => x - y);
  // Gap: no candidate maps back, so use the pre-transition offset (shifts the time forward).
  return new Date(valid[0] ?? before);
}

/** UTC instant of wall-clock `local` (`YYYY-MM-DDTHH:MM[:SS]`) in `tz`. */
export function fromLocal(local: string, tz: string): Date {
  const [y, mo, d, h, mi, s] = local.split(/[-T:]/).map((x) => Number(x));
  return fromWallClock(dayNumber(y ?? 0, mo ?? 1, d ?? 1), { h: h ?? 0, m: mi ?? 0 }, tz, s ?? 0);
}

export const isoUtc = (d: Date) => d.toISOString().replace(/\.\d{3}Z$/, "Z");
